import { AppRoutes } from '../AppRoutes';
import { useTranslation } from '@/hooks/useTranslation';
import { FeatureEnum, PermissionEnum } from '@/graphql/generated';
import { createLazyComponent } from '@/utils/lazyImport';

// Synchronous imports for layouts and guards
import { ProjectLayout } from '@/components/layout/ProjectLayout';
import { FocusLayout } from '@/components/layout/FocusLayout';
import { LoggedInOnlyGuard } from '@/guards/LoggedInOnlyGuard';
import { FeatureGuard } from '@/guards/FeatureGuard';
import { ConsultingCompanyAdministratorOnlyGuard } from '@/guards/ConsultingCompanyAdministratorOnlyGuard';
import { EnterpriseLicenseGuard } from '@/guards/EnterpriseLicenseGuard';

// Lazy-loaded screens
const screens = {
  Home: createLazyComponent(
    () => import('@/screens/project/home/HomeScreen'),
    'HomeScreen',
  ),
  CoachsSettings: createLazyComponent(
    () => import('@/screens/project/coach/settings/CoachsSettingsScreen'),
    'CoachsSettingsScreen',
  ),
  CompanySettings: createLazyComponent(
    () => import('@/screens/project/coach/settings/CompanySettingsScreen'),
    'CompanySettingsScreen',
  ),
  TemplatesSettings: createLazyComponent(
    () => import('@/screens/project/coach/settings/TemplatesSettingsScreen'),
    'TemplatesSettingsScreen',
  ),
  Referential: createLazyComponent(
    () => import('@/screens/project/referential/ReferentialScreen'),
    'ReferentialScreen',
  ),
  Iro: createLazyComponent(
    () => import('@/screens/project/iro/evaluation/IroScreen'),
    'IroScreen',
  ),
  IroSummary: createLazyComponent(
    () => import('@/screens/project/iro/summary/IroSummaryScreen'),
    'IroSummaryScreen',
  ),
  IroSettings: createLazyComponent(
    () => import('@/screens/project/iro/settings/IroSettingsScreen'),
    'IroSettingsScreen',
  ),
  IroMaterialityThreshold: createLazyComponent(
    () =>
      import('@/screens/project/iro/thresholds/IroMaterialityThresholdScreen'),
    'IroMaterialityThresholdScreen',
  ),
  IroList: createLazyComponent(
    () => import('@/screens/project/iro/list/IroListScreen'),
    'IroListScreen',
  ),
  PrioritizationMatrix: createLazyComponent(
    () =>
      import(
        '@/screens/project/publications/prioritizationMatrix/PrioritizationMatrixScreen'
      ),
    'PrioritizationMatrixScreen',
  ),
  MaterialityMatrix: createLazyComponent(
    () => import('@/screens/project/publications/MaterialityMatrixScreen'),
    'MaterialityMatrixScreen',
  ),
  DoubleMaterialityMatrixIro: createLazyComponent(
    () =>
      import(
        '@/screens/project/publications/doubleMaterialityMatrix/DoubleMaterialityMatrixIroScreen'
      ),
    'DoubleMaterialityMatrixIroScreen',
  ),
  DoubleMaterialityMatrixStakeholder: createLazyComponent(
    () =>
      import(
        '@/screens/project/publications/doubleMaterialityMatrix/DoubleMaterialityMatrixStakeholderScreen'
      ),
    'DoubleMaterialityMatrixStakeholderScreen',
  ),
  Roadmap: createLazyComponent(
    () => import('@/screens/project/publications/roadmap/RoadmapScreen'),
    'RoadmapScreen',
  ),
  Action: createLazyComponent(
    () => import('@/screens/project/publications/roadmap/ActionScreen'),
    'ActionScreen',
  ),
  ActionEdit: createLazyComponent(
    () => import('@/screens/project/publications/roadmap/ActionEditScreen'),
    'ActionEditScreen',
  ),
  Maturity: createLazyComponent(
    () => import('@/screens/project/publications/MaturityScreen'),
    'MaturityScreen',
  ),
  MaturityEvaluationsList: createLazyComponent(
    () => import('@/screens/project/maturity/MaturityEvaluationsListScreen'),
    'MaturityEvaluationsListScreen',
  ),
  MaturityEvaluationImport: createLazyComponent(
    () => import('@/screens/project/maturity/MaturityEvaluationImportScreen'),
    'MaturityEvaluationImportScreen',
  ),
  MaturityEvaluation: createLazyComponent(
    () => import('@/screens/project/maturity/MaturityEvaluationScreen'),
    'MaturityEvaluationScreen',
  ),
  MaturityEvaluationAssign: createLazyComponent(
    () => import('@/screens/project/maturity/MaturityEvaluationAssignScreen'),
    'MaturityEvaluationAssignScreen',
  ),
  SurveyList: createLazyComponent(
    () => import('@/screens/project/survey/SurveyList/SurveyListScreen'),
    'SurveyListScreen',
  ),
  ProSurveyResults: createLazyComponent(
    () => import('@/screens/project/survey/ProSurveyResultsScreen'),
    'ProSurveyResultsScreen',
  ),
  Survey: createLazyComponent(
    () => import('@/screens/project/survey/SurveyScreen'),
    'SurveyScreen',
  ),
  ProConsultationRawMateriality: createLazyComponent(
    () =>
      import(
        '@/screens/project/survey/ConsultationRawMateriality/ProConsultationRawMaterialityScreen'
      ),
    'ProConsultationRawMaterialityScreen',
  ),
  ProStakeholder: createLazyComponent(
    () => import('@/screens/project/stakeholder/ProStakeholderScreen'),
    'ProStakeholderScreen',
  ),
  ProjectNotebook: createLazyComponent(
    () => import('@/screens/project/project/ProjectNotebookScreen'),
    'ProjectNotebookScreen',
  ),
  ProjectSettings: createLazyComponent(
    () => import('@/screens/project/project/ProjectSettingsScreen'),
    'ProjectSettingsScreen',
  ),
  ProjectCompany: createLazyComponent(
    () => import('@/screens/project/project/ProjectCompanyScreen'),
    'ProjectCompanyScreen',
  ),
  ProjectUsers: createLazyComponent(
    () => import('@/screens/project/project/ProjectUsersScreen'),
    'ProjectUsersScreen',
  ),
  ProjectMethodology: createLazyComponent(
    () => import('@/screens/project/project/ProjectMethodologyScreen'),
    'ProjectMethodologyScreen',
  ),
  ProjectRoles: createLazyComponent(
    () => import('@/screens/project/project/ProjectRolesScreen'),
    'ProjectRolesScreen',
  ),
  ReportIndicatorCollectCarbonHubImport: createLazyComponent(
    () =>
      import(
        '@/screens/project/indicator/carbonHub/import/ReportIndicatorCollectCarbonHubImportScreen'
      ),
    'ReportIndicatorCollectCarbonHubImportScreen',
  ),
  ResourcesManagement: createLazyComponent(
    () => import('@/screens/project/resources/ResourcesManagementScreen'),
    'ResourcesManagementScreen',
  ),
  SymfonyResourcesManagement: createLazyComponent(
    () => import('@/pages/resources/SymfonyResourcesManagement'),
    'SymfonyResourcesManagement',
  ),
  IndicatorsCollect: createLazyComponent(
    () => import('@/screens/project/indicatorsCollect/IndicatorsCollectScreen'),
    'IndicatorsCollectScreen',
  ),
  EditIndicatorCollect: createLazyComponent(
    () =>
      import(
        '@/screens/project/indicatorsCollect/editIndicator/EditIndicatorScreen'
      ),
    'EditIndicatorCollectScreen',
  ),
  EditDimensionItemCollect: createLazyComponent(
    () =>
      import(
        '@/screens/project/indicatorsCollect/editDimensionItem/EditDimensionItemScreen'
      ),
    'EditDimensionItemCollectScreen',
  ),
  ReportHomeRedirect: createLazyComponent(
    () =>
      import(
        '@/screens/project/reportDisclosureRequirements/ReportHomeRedirectScreen'
      ),
    'ReportHomeRedirectScreen',
  ),
  ReportDisclosureRequirements: createLazyComponent(
    () =>
      import(
        '@/screens/project/reportDisclosureRequirements/disclosureRequirements/ReportDisclosureRequirementsScreen'
      ),
    'ReportDisclosureRequirementsScreen',
  ),
  ReportIndicators: createLazyComponent(
    () =>
      import(
        '@/screens/project/reportDisclosureRequirements/indicators/ReportIndicatorsScreen'
      ),
    'ReportIndicatorsScreen',
  ),
  ReportSynthesis: createLazyComponent(
    () =>
      import(
        '@/screens/project/reportDisclosureRequirements/synthesis/ReportSynthesisScreen'
      ),
    'ReportSynthesisScreen',
  ),
  ReportChecklist: createLazyComponent(
    () =>
      import(
        '@/screens/project/reportDisclosureRequirements/checklist/ReportChecklistScreen'
      ),
    'ReportChecklistScreen',
  ),
  ReportDisclosureRequirementGapAnalysis: createLazyComponent(
    () =>
      import(
        '@/screens/project/reportDisclosureRequirement/ReportDisclosureRequirementGapAnalysisScreen'
      ),
    'ReportDisclosureRequirementGapAnalysisScreen',
  ),
  ReportDisclosureRequirementIndicatorsCollect: createLazyComponent(
    () =>
      import(
        '@/screens/project/reportDisclosureRequirement/ReportDisclosureRequirementIndicatorsCollectScreen'
      ),
    'ReportDisclosureRequirementIndicatorsCollectScreen',
  ),
  Reports: createLazyComponent(
    () => import('@/screens/project/reports/ReportsScreen'),
    'ReportsScreen',
  ),
  Dimensions: createLazyComponent(
    () => import('@/screens/project/reports/DimensionsScreen'),
    'DimensionsScreen',
  ),
};

export const useAppRouteConfig = () => {
  const { t } = useTranslation();

  return [
    {
      path: AppRoutes.Home,
      element: (
        <LoggedInOnlyGuard>
          <ProjectLayout />
        </LoggedInOnlyGuard>
      ),
      children: [
        {
          index: true,
          breadcrumb: null,
          element: <screens.Home />,
        },
        {
          path: AppRoutes.Stakes,
          element: (
            <FeatureGuard feature={FeatureEnum.ReferentialManagement}>
              <screens.Referential />
            </FeatureGuard>
          ),
          breadcrumb: t('nav:page.referential'),
        },
        {
          path: AppRoutes.Iro,
          children: [
            {
              index: true,
              element: (
                <FeatureGuard feature={FeatureEnum.IroCotation}>
                  <screens.IroSummary />
                </FeatureGuard>
              ),
              breadcrumb: t('nav:page.iro_rating'),
            },
            {
              path: AppRoutes.IroSettings,
              element: (
                <FeatureGuard
                  feature={FeatureEnum.IroCotation}
                  requiredPermission={PermissionEnum.Write}
                >
                  <screens.IroSettings />
                </FeatureGuard>
              ),
              breadcrumb: t('nav:page.iro.settings'),
            },
            {
              path: AppRoutes.IroMaterialityThresholds,
              element: (
                <FeatureGuard feature={FeatureEnum.IroCotation}>
                  <screens.IroMaterialityThreshold />
                </FeatureGuard>
              ),
              breadcrumb: t('nav:page.iro.materiality_thresholds'),
            },
            {
              path: AppRoutes.IroList,
              element: (
                <FeatureGuard feature={FeatureEnum.IroCotation}>
                  <screens.IroList />
                </FeatureGuard>
              ),
              breadcrumb: t('nav:page.iro.byIro'),
            },
            {
              path: AppRoutes.IroRating,
              breadcrumb: '',
              children: [
                {
                  index: true,
                  element: (
                    <FeatureGuard feature={FeatureEnum.IroCotation}>
                      <screens.Iro />
                    </FeatureGuard>
                  ),
                  breadcrumb: t('screen.iro.iro_rating'),
                },
              ],
            },
          ],
        },
        {
          path: AppRoutes.ReportHome,
          breadcrumb: t('nav:page.referential'),
          children: [
            {
              index: true,
              element: (
                <FeatureGuard feature={FeatureEnum.GapAnalysisManagement}>
                  <screens.ReportHomeRedirect />
                </FeatureGuard>
              ),
              breadcrumb: null,
            },
            {
              element: (
                <FeatureGuard feature={FeatureEnum.GapAnalysisManagement}>
                  <screens.ReportDisclosureRequirements />
                </FeatureGuard>
              ),
              breadcrumb: null,
              path: AppRoutes.ReportDisclosureRequirements,
            },
            {
              element: (
                <FeatureGuard feature={FeatureEnum.GapAnalysisManagement}>
                  <screens.ReportIndicators />
                </FeatureGuard>
              ),
              breadcrumb: null,
              path: AppRoutes.ReportIndicators,
            },
            {
              element: (
                <FeatureGuard feature={FeatureEnum.GapAnalysisManagement}>
                  <screens.ReportSynthesis />
                </FeatureGuard>
              ),
              breadcrumb: null,
              path: AppRoutes.ReportSynthesis,
            },
            {
              element: (
                <FeatureGuard feature={FeatureEnum.GapAnalysisManagement}>
                  <screens.ReportChecklist />
                </FeatureGuard>
              ),
              breadcrumb: null,
              path: AppRoutes.ReportCheckList,
            },
            {
              path: AppRoutes.ReportCarbonHubImport,
              element: (
                <FocusLayout>
                  <FeatureGuard
                    feature={FeatureEnum.IndicatorsCollect}
                    requiredPermission={PermissionEnum.Write}
                  >
                    <screens.ReportIndicatorCollectCarbonHubImport />
                  </FeatureGuard>
                </FocusLayout>
              ),
              breadcrumb: null,
            },
          ],
        },
        {
          path: AppRoutes.Reports,
          children: [
            {
              index: true,
              element: (
                <FeatureGuard feature={FeatureEnum.IndicatorsManagement}>
                  <screens.Reports />
                </FeatureGuard>
              ),
            },
            {
              element: (
                <FeatureGuard feature={FeatureEnum.IndicatorsManagement}>
                  <screens.Dimensions />
                </FeatureGuard>
              ),
              path: AppRoutes.ReportsDimensions,
            },
          ],
        },
        {
          path: AppRoutes.ReportDisclosureRequirement,
          children: [
            {
              element: (
                <FeatureGuard feature={FeatureEnum.GapAnalysisManagement}>
                  <screens.ReportDisclosureRequirementGapAnalysis />
                </FeatureGuard>
              ),
              breadcrumb: null,
              path: AppRoutes.ReportDisclosureRequirementGapAnalysis,
            },
            {
              element: (
                <EnterpriseLicenseGuard>
                  <FeatureGuard feature={FeatureEnum.IndicatorsManagement}>
                    <screens.ReportDisclosureRequirementIndicatorsCollect />
                  </FeatureGuard>
                </EnterpriseLicenseGuard>
              ),
              breadcrumb: null,
              path: AppRoutes.ReportDisclosureRequirementCollectIndicators,
            },
          ],
        },
        {
          path: AppRoutes.StakeholderMapping,
          element: (
            <FeatureGuard feature={FeatureEnum.StakeholderManagement}>
              <screens.ProStakeholder />
            </FeatureGuard>
          ),
          breadcrumb: t('nav:page.stakeholder_mapping'),
        },
        {
          path: AppRoutes.Project,
          breadcrumb: null,
          children: [
            {
              index: true,
              element: (
                <FeatureGuard feature={FeatureEnum.ProjectMethodology}>
                  <screens.ProjectMethodology />
                </FeatureGuard>
              ),
              breadcrumb: null,
            },
            {
              path: AppRoutes.ProjectNotebook,
              element: (
                <FeatureGuard feature={FeatureEnum.ProjectNotes}>
                  <screens.ProjectNotebook />
                </FeatureGuard>
              ),
              breadcrumb: null,
            },
            {
              path: AppRoutes.ProjectCompany,
              element: (
                <FeatureGuard feature={FeatureEnum.ProjectCompany}>
                  <screens.ProjectCompany />
                </FeatureGuard>
              ),
              breadcrumb: null,
            },
            {
              path: AppRoutes.ProjectUsers,
              element: (
                <FeatureGuard feature={FeatureEnum.ProjectUsers}>
                  <screens.ProjectUsers />
                </FeatureGuard>
              ),
              breadcrumb: null,
            },
            {
              path: AppRoutes.ProjectRoles,
              element: (
                <FeatureGuard feature={FeatureEnum.ProjectUsers}>
                  <screens.ProjectRoles />
                </FeatureGuard>
              ),
              breadcrumb: null,
            },
            {
              path: AppRoutes.ProjectSettings,
              element: (
                <FeatureGuard feature={FeatureEnum.ProjectCompany}>
                  <screens.ProjectSettings />
                </FeatureGuard>
              ),
              breadcrumb: null,
            },
          ],
        },
        {
          path: AppRoutes.Publication,
          breadcrumb: null,
          children: [
            {
              path: AppRoutes.PublicationMaturity,
              breadcrumb: t('nav:page.maturity_score'),
              element: (
                <FeatureGuard feature={FeatureEnum.DeliverableMaturityScore}>
                  <screens.Maturity />
                </FeatureGuard>
              ),
            },
            {
              path: AppRoutes.PublicationPrioritizationMatrix,
              breadcrumb: t('nav:page.prioritization_matrix'),
              element: (
                <FeatureGuard
                  feature={FeatureEnum.DeliverablePrioritizationMatrix}
                >
                  <screens.PrioritizationMatrix />
                </FeatureGuard>
              ),
            },
            {
              path: AppRoutes.PublicationMaterialityMatrix,
              breadcrumb: t('nav:page.materiality_matrix'),
              element: (
                <FeatureGuard
                  feature={FeatureEnum.DeliverableMaterialityMatrix}
                >
                  <screens.MaterialityMatrix />
                </FeatureGuard>
              ),
            },
            {
              path: AppRoutes.PublicationDoubleMaterialityMatrixIro,
              breadcrumb: t('nav:page.double_materiality_matrix'),
              element: (
                <FeatureGuard
                  feature={FeatureEnum.DeliverableDoubleMaterialityMatrix}
                >
                  <screens.DoubleMaterialityMatrixIro />
                </FeatureGuard>
              ),
            },
            {
              path: AppRoutes.PublicationDoubleMaterialityMatrixStakeholder,
              breadcrumb: t('nav:page.double_materiality_matrix'),
              element: (
                <FeatureGuard
                  feature={FeatureEnum.DeliverableDoubleMaterialityMatrix}
                >
                  <screens.DoubleMaterialityMatrixStakeholder />
                </FeatureGuard>
              ),
            },
          ],
        },
        {
          path: AppRoutes.Roadmap,
          children: [
            {
              index: true,
              breadcrumb: t('nav:page.roadmap'),
              element: (
                <FeatureGuard feature={FeatureEnum.RoadmapManagement}>
                  <screens.Roadmap />
                </FeatureGuard>
              ),
            },
            {
              path: AppRoutes.RoadmapAction,
              breadcrumb: null,
              element: (
                <FeatureGuard feature={FeatureEnum.RoadmapManagement}>
                  <screens.Action />
                </FeatureGuard>
              ),
            },
            {
              path: AppRoutes.RoadmapActionEdit,
              breadcrumb: null,
              element: (
                <FeatureGuard
                  feature={FeatureEnum.RoadmapManagement}
                  requiredPermission={PermissionEnum.Write}
                >
                  <screens.ActionEdit />
                </FeatureGuard>
              ),
            },
          ],
        },
        {
          path: AppRoutes.Survey,
          breadcrumb: null,
          children: [
            {
              path: AppRoutes.Survey,
              breadcrumb: null,
              children: [
                {
                  index: true,
                  breadcrumb: t('nav:page.surveys'),
                  element: (
                    <FeatureGuard feature={FeatureEnum.ConsultationManagement}>
                      <screens.SurveyList />
                    </FeatureGuard>
                  ),
                },
                {
                  path: AppRoutes.SurveyResults,
                  children: [
                    {
                      index: true,
                      element: (
                        <FeatureGuard feature={FeatureEnum.ConsultationResults}>
                          <screens.ProSurveyResults />
                        </FeatureGuard>
                      ),
                      breadcrumb: t('nav:page.surveys'),
                    },
                  ],
                },
                {
                  path: AppRoutes.SurveyItem,
                  children: [
                    {
                      index: true,
                      element: (
                        <FeatureGuard
                          feature={FeatureEnum.ConsultationManagement}
                        >
                          <screens.Survey />
                        </FeatureGuard>
                      ),
                      breadcrumb: null,
                    },
                  ],
                },
                {
                  path: AppRoutes.SurveyTab,
                  children: [
                    {
                      index: true,
                      element: (
                        <FeatureGuard
                          feature={FeatureEnum.ConsultationManagement}
                        >
                          <screens.Survey />
                        </FeatureGuard>
                      ),
                      breadcrumb: null,
                    },
                  ],
                },
              ],
            },
          ],
        },
        {
          path: AppRoutes.ConsultationRawMaterialities,
          breadcrumb: null,
          children: [
            {
              index: true,
              breadcrumb: t('nav:page.surveys'),
              element: (
                <FeatureGuard feature={FeatureEnum.ConsultationManagement}>
                  <screens.ProConsultationRawMateriality />
                </FeatureGuard>
              ),
            },
          ],
        },
        {
          path: AppRoutes.Resources,
          breadcrumb: null,
          children: [
            {
              index: true,
              breadcrumb: t('nav:page.resource_management'),
              element: (
                <FeatureGuard feature={FeatureEnum.ResourcesManagement}>
                  <screens.ResourcesManagement />
                </FeatureGuard>
              ),
            },
          ],
        },
        {
          path: AppRoutes.ResourcesSymfony,
          breadcrumb: null,
          children: [
            {
              index: true,
              breadcrumb: `${t('nav:page.resource_management')} (Symfony API)`,
              element: (
                <FeatureGuard feature={FeatureEnum.ResourcesManagement}>
                  <screens.SymfonyResourcesManagement />
                </FeatureGuard>
              ),
            },
          ],
        },
        {
          path: AppRoutes.IndicatorsCollect,
          breadcrumb: null,
          children: [
            {
              index: true,
              breadcrumb: t('nav:page.indicators_collect'),
              element: (
                <EnterpriseLicenseGuard>
                  <FeatureGuard feature={FeatureEnum.IndicatorsCollect}>
                    <screens.IndicatorsCollect />
                  </FeatureGuard>
                </EnterpriseLicenseGuard>
              ),
            },
            {
              path: AppRoutes.IndicatorsCollectEditIndicator,
              element: (
                <EnterpriseLicenseGuard>
                  <FocusLayout>
                    <FeatureGuard feature={FeatureEnum.IndicatorsCollect}>
                      <screens.EditIndicatorCollect />
                    </FeatureGuard>
                  </FocusLayout>
                </EnterpriseLicenseGuard>
              ),
            },
            {
              path: AppRoutes.IndicatorsCollectEditOrganizationUnitIndicator,
              element: (
                <EnterpriseLicenseGuard>
                  <FocusLayout>
                    <FeatureGuard feature={FeatureEnum.IndicatorsCollect}>
                      <screens.EditIndicatorCollect />
                    </FeatureGuard>
                  </FocusLayout>
                </EnterpriseLicenseGuard>
              ),
            },
            {
              path: AppRoutes.IndicatorsCollectEditDimensionItem,
              element: (
                <EnterpriseLicenseGuard>
                  <FocusLayout>
                    <FeatureGuard feature={FeatureEnum.IndicatorsCollect}>
                      <screens.EditDimensionItemCollect />
                    </FeatureGuard>
                  </FocusLayout>
                </EnterpriseLicenseGuard>
              ),
            },
          ],
        },
        {
          path: AppRoutes.MaturityEvaluations,
          breadcrumb: null,
          children: [
            {
              index: true,
              breadcrumb: t('maturity.evaluation.title'),
              element: (
                <FeatureGuard
                  feature={FeatureEnum.MaturityEvaluationManagement}
                >
                  <screens.MaturityEvaluationsList />
                </FeatureGuard>
              ),
            },
            {
              path: AppRoutes.MaturityEvaluationImport,
              breadcrumb: t('nav:page.maturity_evaluation_import'),
              element: (
                <FeatureGuard
                  feature={FeatureEnum.MaturityEvaluationManagement}
                  requiredPermission={PermissionEnum.Write}
                >
                  <screens.MaturityEvaluationImport />
                </FeatureGuard>
              ),
            },
            {
              path: AppRoutes.MaturityEvaluationItem,
              breadcrumb: null,
              element: (
                <FeatureGuard
                  feature={FeatureEnum.MaturityEvaluationManagement}
                  requiredPermission={PermissionEnum.Write}
                >
                  <screens.MaturityEvaluation />
                </FeatureGuard>
              ),
            },
            {
              path: AppRoutes.MaturityEvaluationItemTab,
              children: [
                {
                  index: true,
                  element: (
                    <FeatureGuard
                      feature={FeatureEnum.MaturityEvaluationManagement}
                      requiredPermission={PermissionEnum.Write}
                    >
                      <screens.MaturityEvaluation />
                    </FeatureGuard>
                  ),
                  breadcrumb: null,
                },
              ],
            },
            {
              path: AppRoutes.MaturityEvaluationAssign,
              breadcrumb: t('nav:page.maturity_evaluation_assign'),
              element: (
                <FeatureGuard
                  feature={FeatureEnum.MaturityEvaluationManagement}
                  requiredPermission={PermissionEnum.Write}
                >
                  <screens.MaturityEvaluationAssign />
                </FeatureGuard>
              ),
            },
          ],
        },
        {
          path: AppRoutes.Settings,
          breadcrumb: null,
          children: [
            {
              path: AppRoutes.SettingsCoach,
              element: (
                <ConsultingCompanyAdministratorOnlyGuard>
                  <screens.CoachsSettings />
                </ConsultingCompanyAdministratorOnlyGuard>
              ),
              breadcrumb: t('nav:page.coach_management'),
            },
            {
              path: AppRoutes.SettingsCompany,
              element: (
                <ConsultingCompanyAdministratorOnlyGuard>
                  <screens.CompanySettings />
                </ConsultingCompanyAdministratorOnlyGuard>
              ),
              breadcrumb: t('nav:page.company_management'),
            },
            {
              path: AppRoutes.SettingsTemplates,
              element: (
                <ConsultingCompanyAdministratorOnlyGuard>
                  <screens.TemplatesSettings />
                </ConsultingCompanyAdministratorOnlyGuard>
              ),
              breadcrumb: t('nav:page.templates_management'),
            },
            {
              path: AppRoutes.ActionTemplateEdit,
              element: (
                <ConsultingCompanyAdministratorOnlyGuard>
                  <screens.ActionEdit />
                </ConsultingCompanyAdministratorOnlyGuard>
              ),
            },
          ],
        },
      ],
    },
  ];
};
